import React, { useState } from 'react';
import { format } from 'date-fns';
import { ExpandIcon } from '../../Global/Icons';
import { FadeModal, NewLinkBadge } from '../../Global/Transitions';
import { Retest } from '../../../Models/Retest';
import RetestFindings from '../../Popups/Projects/RetestFindings';

interface RetestRowProps {
	retest: Retest;
	projectId: string;
	canopyId?: number;
}

// disposition is ours, retestStatusAfter is Canopy's free-form string — they're independent, so
// bucket each finding into exactly one outcome to keep the counts mutually exclusive and
// `outstanding` non-negative.
const outcomeFor = (retest: Retest): string => {
	if (retest.status === 'Cancelled') return 'quote rejected';
	if (retest.status !== 'Complete') return '—';

	let resolved = 0;
	let accepted = 0;
	let outstanding = 0;
	retest.findings.forEach((f) => {
		if (f.disposition === 'RiskAccepted') accepted++;
		else if (f.retestStatusAfter === 'Resolved') resolved++;
		else outstanding++;
	});

	return `${resolved} resolved · ${accepted} accepted · ${outstanding} not resolved`;
};

const RetestRow: React.FC<RetestRowProps> = ({ retest, projectId, canopyId }) => {
	const [show, setShow] = useState(false);

	return (
		// The modal is a SIBLING of the row, never a child. React events bubble through the React
		// tree rather than the DOM, so a modal nested inside the row would send every click within
		// it — Close included — back into the row and reopen itself instantly.
		<>
			{/* A row-level onClick rather than a stretched-link: this app's `.table td` is
			    position: relative (styles/App.css), which contains a stretched-link inside its own
			    <td> so it can never cover the row. An onClick on a <tr> is neither focusable nor
			    announced, so the real <button> below stays in the row to carry both. */}
			<tr className='pe-link' onClick={() => setShow(true)}>
				<td width={70}>{retest.retestNumber}</td>
				<td>{format(new Date(retest.requestedDate), 'dd/MM/yyyy')}</td>
				<td>{outcomeFor(retest)}</td>
				<td style={{ minWidth: 180 }}>
					<NewLinkBadge bullet status={retest.status}>{retest.status}</NewLinkBadge>
				</td>
				<td width={60}>
					<button
						type='button'
						className='btn btn-link-secondary p-0'
						// "Retest 2" alone would be read out as just its number by a screen reader, which
						// says nothing about what the control does.
						aria-label={`View findings for retest ${retest.retestNumber}`}
						// Also handled by the row's onClick this bubbles into. Kept explicit rather than
						// relying on that: both set the same state to true, so the double call is a
						// no-op, and the button still works if the row ever stops handling clicks.
						onClick={() => setShow(true)}
					>
						<ExpandIcon />
					</button>
				</td>
			</tr>
			<FadeModal show={show} size='lg' onHide={() => setShow(false)}>
				<RetestFindings
					retest={retest}
					projectId={projectId}
					canopyId={canopyId}
					onClose={() => setShow(false)}
				/>
			</FadeModal>
		</>
	);
};

export default RetestRow;
